import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Clock, FileText, Brain, Thermometer, Target, Dices } from 'lucide-react';
import { AgentExecutionFlow } from '../types';
import { SearchedOpinionsList } from './SearchedOpinionsList';
import { SystemPromptViewer } from './SystemPromptViewer';
import { SearchParametersDisplay } from './SearchParametersDisplay';
import { ToolCallCard } from './ToolCallCard';
import { FolioStepCard } from './FolioStepCard';

interface ExecutionStepsViewerProps {
  executionFlow: AgentExecutionFlow;
}

export const ExecutionStepsViewer: React.FC<ExecutionStepsViewerProps> = ({ executionFlow }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [wrapStates, setWrapStates] = useState<Map<string, { input: boolean; output: boolean }>>(new Map());
  const [selectedPromptHash, setSelectedPromptHash] = useState<{ hash: string; agentType: string } | null>(null);

  // Safety check
  if (!executionFlow) {
    return null;
  }

  if (!executionFlow.executionSteps) {
    return null;
  }

  const toggleStepExpanded = (index: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleWrap = (stepKey: string, field: 'input' | 'output') => {
    setWrapStates(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(stepKey) || { input: false, output: false };
      newMap.set(stepKey, {
        ...current,
        [field]: !current[field]
      });
      return newMap;
    });
  };

  const getWrapState = (stepKey: string, field: 'input' | 'output'): boolean => {
    return wrapStates.get(stepKey)?.[field] || false;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getStepDisplayName = (agentType: string): string => {
    if (!agentType) return 'Unknown step';
    const agentTypeLower = String(agentType).toLowerCase();
    switch (agentTypeLower) {
      case 'orchestrator':
        return 'Planning approach';
      case 'researcher':
        return 'Generating search parameters';
      case 'folio':
        return 'Expanding legal ontology (FOLIO)';
      case 'searcher':
        return 'Searching knowledge base';
      case 'analyzer':
        return 'Analyzing content';
      case 'synthesizer':
        return 'Synthesizing response';
      case 'validator':
        return 'Validating results';
      default:
        return agentType;
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'text-gray-600';
    const statusLower = String(status).toLowerCase();
    switch (statusLower) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'inprogress':
        return 'text-blue-600';
      case 'blocked':
        return 'text-yellow-600';
      case 'needsclarification':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    if (!status) return '⏳';
    const statusLower = String(status).toLowerCase();
    switch (statusLower) {
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'inprogress':
        return '⚙️';
      case 'blocked':
        return '🚫';
      case 'needsclarification':
        return '❓';
      default:
        return '⏳';
    }
  };

  // Build chronological flow items
  const flowItems: Array<{
    type: 'overview' | 'query' | 'decision' | 'folio' | 'step' | 'searchContext' | 'embedding' | 'opinions' | 'response';
    timestamp: Date;
    data: any;
    stepIndex?: number;
    timing?: {
      parameterGenerationTimeMs?: number;
      embeddingGenerationTimeMs?: number;
      searchExecutionTimeMs?: number;
    };
  }> = [];

  // Add overview
  flowItems.push({
    type: 'overview',
    timestamp: new Date(executionFlow.startTime),
    data: executionFlow
  });

  // Add user query
  flowItems.push({
    type: 'query',
    timestamp: new Date(executionFlow.startTime),
    data: executionFlow.userCommand
  });

  // Add orchestrator decision
  if (executionFlow.orchestratorDecision) {
    flowItems.push({
      type: 'decision',
      timestamp: new Date(executionFlow.startTime),
      data: executionFlow.orchestratorDecision
    });
  }

  // Add FOLIO expansion
  if (executionFlow.folioExpansion) {
    // Place after decision but before search steps
    const folioTimestamp = executionFlow.orchestratorDecision
      ? new Date(new Date(executionFlow.startTime).getTime() + 1)
      : new Date(executionFlow.startTime);
    flowItems.push({
      type: 'folio',
      timestamp: folioTimestamp,
      data: executionFlow.folioExpansion
    });
  }

  // Add execution steps
  executionFlow.executionSteps.forEach((step, index) => {
    flowItems.push({
      type: 'step',
      timestamp: new Date(step.startTime),
      data: step,
      stepIndex: index
    });
  });

  // Add search contexts (if available) - place them chronologically
  if (executionFlow.searchContexts && executionFlow.searchContexts.length > 0) {
    executionFlow.searchContexts.forEach((context, index) => {
      flowItems.push({
        type: 'searchContext',
        timestamp: new Date(context.timestamp),
        data: context,
        stepIndex: index
      });

      // Add embedding generation as a separate step after search parameters
      if (context.embeddingGenerationTimeMs !== undefined && context.embeddingGenerationTimeMs > 0) {
        flowItems.push({
          type: 'embedding',
          timestamp: new Date(new Date(context.timestamp).getTime() + 1),
          data: {
            embeddingGenerationTimeMs: context.embeddingGenerationTimeMs,
            attemptNumber: context.attemptNumber,
            vectorQueryCount: context.totalEmbeddingCount ?? context.vectorQueryText?.length ?? 0,
            embeddingTokensUsed: context.embeddingTokensUsed ?? 0,
            cachedEmbeddingTokens: context.cachedEmbeddingTokens ?? 0
          },
          stepIndex: index
        });
      }
    });
  }

  // Add searched opinions (if available and after search contexts)
  if (executionFlow.searchedOpinions && executionFlow.searchedOpinions.length > 0) {
    // Find the last search context to place opinions after it
    const lastSearchContext = executionFlow.searchContexts && executionFlow.searchContexts.length > 0
      ? executionFlow.searchContexts[executionFlow.searchContexts.length - 1]
      : null;
    
    const opinionTimestamp = lastSearchContext
      ? new Date(new Date(lastSearchContext.timestamp).getTime() + 1) // 1ms after search context
      : new Date(executionFlow.startTime);
    
    flowItems.push({
      type: 'opinions',
      timestamp: opinionTimestamp,
      data: executionFlow.searchedOpinions,
      timing: lastSearchContext ? {
        parameterGenerationTimeMs: lastSearchContext.parameterGenerationTimeMs,
        embeddingGenerationTimeMs: lastSearchContext.embeddingGenerationTimeMs,
        searchExecutionTimeMs: lastSearchContext.searchExecutionTimeMs
      } : undefined
    });
  }

  // Add final response
  if (executionFlow.finalResponse) {
    flowItems.push({
      type: 'response',
      timestamp: executionFlow.endTime ? new Date(executionFlow.endTime) : new Date(),
      data: executionFlow.finalResponse
    });
  }

  // Sort by timestamp
  flowItems.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  // Calculate total cost and tokens
  const calculateTotalCost = () => {
    let totalCost = 0;
    let totalInputTokens = 0;
    let totalOutputTokens = 0;
    let totalCachedTokens = 0;
    let totalEmbeddingTokens = 0;
    let totalEmbeddingCost = 0;
    let totalCachedEmbeddingTokens = 0;
    let totalCachedEmbeddingCost = 0;
    const costByAgent: Record<string, number> = {};
    
    executionFlow.executionSteps.forEach(step => {
      if (step.costDetail) {
        const inputCost = (step.costDetail.inputTokens / 1_000_000) * step.costDetail.costPerMillionInputTokens;
        const outputCost = (step.costDetail.outputTokens / 1_000_000) * step.costDetail.costPerMillionOutputTokens;
        const stepCost = inputCost + outputCost;
        
        totalCost += stepCost;
        totalInputTokens += step.costDetail.inputTokens;
        totalOutputTokens += step.costDetail.outputTokens;
        totalCachedTokens += step.costDetail.cachedTokens || 0;
        
        const agentType = step.agentType;
        costByAgent[agentType] = (costByAgent[agentType] || 0) + stepCost;
      }
      // Include tool call costs
      if (step.toolCalls) {
        step.toolCalls.forEach(tc => {
          if (tc.costDetail) {
            const tcInputCost = (tc.costDetail.inputTokens / 1_000_000) * tc.costDetail.costPerMillionInputTokens;
            const tcOutputCost = (tc.costDetail.outputTokens / 1_000_000) * tc.costDetail.costPerMillionOutputTokens;
            const tcCost = tcInputCost + tcOutputCost;
            totalCost += tcCost;
            totalInputTokens += tc.costDetail.inputTokens;
            totalOutputTokens += tc.costDetail.outputTokens;
            totalCachedTokens += tc.costDetail.cachedTokens || 0;
            costByAgent[step.agentType] = (costByAgent[step.agentType] || 0) + tcCost;
          }
        });
      }
    });

    // Include embedding costs from search contexts
    if (executionFlow.searchContexts) {
      executionFlow.searchContexts.forEach(ctx => {
        if (ctx.embeddingTokensUsed && ctx.embeddingTokensUsed > 0) {
          const embCost = (ctx.embeddingTokensUsed / 1_000_000) * 0.12; // Voyage pricing
          totalEmbeddingTokens += ctx.embeddingTokensUsed;
          totalEmbeddingCost += embCost;
          totalCost += embCost;
          costByAgent['Embedding (new)'] = (costByAgent['Embedding (new)'] || 0) + embCost;
        }
        if (ctx.cachedEmbeddingTokens && ctx.cachedEmbeddingTokens > 0) {
          const cachedCost = (ctx.cachedEmbeddingTokens / 1_000_000) * 0.12;
          totalCachedEmbeddingTokens += ctx.cachedEmbeddingTokens;
          totalCachedEmbeddingCost += cachedCost;
          costByAgent['Embedding (cached savings)'] = (costByAgent['Embedding (cached savings)'] || 0) + cachedCost;
        }
      });
    }
    
    return { totalCost, totalInputTokens, totalOutputTokens, totalCachedTokens, totalEmbeddingTokens, totalEmbeddingCost, totalCachedEmbeddingTokens, totalCachedEmbeddingCost, costByAgent };
  };

  const formatCost = (cost: number): string => {
    if (cost < 0.0001) return `$${cost.toFixed(6)}`;
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  const costSummary = calculateTotalCost();

  return (
    <div data-component="ExecutionStepsViewer" className="mt-3 border border-gray-200 rounded-lg bg-gray-50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span>View execution flow</span>
          <span className={`font-semibold whitespace-nowrap ${getStatusColor(executionFlow.status)}`}>
            {getStatusIcon(executionFlow.status)} {String(executionFlow.status).toUpperCase()}
          </span>
          {executionFlow.endTime && executionFlow.totalExecutionTimeMs && executionFlow.totalExecutionTimeMs > 0 && (
            <span className="text-gray-500 font-normal">
              ({formatDuration(executionFlow.totalExecutionTimeMs)})
            </span>
          )}
          {costSummary.totalCost > 0 && (
            <span className="text-green-600 font-semibold">
              {formatCost(costSummary.totalCost)}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="border-t border-gray-200 p-4">
          <div className="space-y-3">
            {flowItems.map((item, index) => {
              switch (item.type) {
                case 'overview':
                  return (
                    <div key={`overview-${index}`} className="bg-white p-3 rounded border">
                      <h4 className="font-medium text-gray-900 mb-2">Execution Overview</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>ID: <span className="font-mono">{item.data.executionId}</span></div>
                        <div>Started: {formatTimestamp(item.data.startTime)}</div>
                        {item.data.endTime && (
                          <div>Completed: {formatTimestamp(item.data.endTime)}</div>
                        )}
                        <div>Duration: {formatDuration(item.data.totalExecutionTimeMs)}</div>
                      </div>
                    </div>
                  );

                case 'query':
                  return (
                    <div key={`query-${index}`} className="bg-white p-3 rounded border">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">User Query</h4>
                        <div className="text-xs text-gray-500">
                          {formatTimestamp(item.timestamp.toISOString())}
                        </div>
                      </div>
                      <div className="text-sm">{item.data}</div>
                    </div>
                  );

                case 'decision':
                  return (
                    <div key={`decision-${index}`} className="bg-blue-50 p-3 rounded border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-blue-900">Orchestrator Decision</h4>
                        {item.data.executionTimeMs && (
                          <div className="text-xs text-blue-600">
                            {formatDuration(item.data.executionTimeMs)}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div><strong>Decision:</strong> {item.data.decision}</div>
                        {item.data.reason && (
                          <div><strong>Reason:</strong> {item.data.reason}</div>
                        )}
                        {item.data.researchPrompt && (
                          <div><strong>Research Prompt:</strong> {item.data.researchPrompt}</div>
                        )}
                      </div>
                    </div>
                  );

                case 'folio':
                  return (
                    <div key={`folio-${index}`}>
                      <FolioStepCard data={item.data} />
                    </div>
                  );

                case 'step':
                  const step = item.data;
                  const isStepExpanded = expandedSteps.has(item.stepIndex!);
                  const stepKey = `step-${item.stepIndex}`;
                  const inputWrapped = getWrapState(stepKey, 'input');
                  const outputWrapped = getWrapState(stepKey, 'output');
                  
                  // Calculate cost if costDetail is available
                  const calculateCost = (costDetail: any): number => {
                    const inputCost = (costDetail.inputTokens / 1_000_000) * costDetail.costPerMillionInputTokens;
                    const outputCost = (costDetail.outputTokens / 1_000_000) * costDetail.costPerMillionOutputTokens;
                    return inputCost + outputCost;
                  };
                  
                  return (
                    <div key={`step-${index}`} className="bg-white p-3 rounded border">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleStepExpanded(item.stepIndex!)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{getStepDisplayName(step.agentType)}</span>
                          {step.toolCalls && step.toolCalls.length > 0 && (
                            <span className="text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                              ({step.toolCalls.length} tool call{step.toolCalls.length !== 1 ? 's' : ''})
                            </span>
                          )}
                          {step.costDetail ? (
                            <div className="flex items-center gap-1">
                              <span className="text-base">💰</span>
                              <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                {formatCost(calculateCost(step.costDetail))} | {step.costDetail.inputTokens + step.costDetail.outputTokens} tokens
                                <span className="text-blue-700"> ({step.costDetail.cachedTokens ?? 0} cached)</span>
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-red-500 bg-red-100 px-2 py-0.5 rounded">No cost data</span>
                          )}
                          {isStepExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDuration(step.executionTimeMs)}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatTimestamp(step.startTime)}
                        {(step.costDetail || step.modelUsed) && (
                          <span className="ml-2 text-gray-700 font-medium">
                            Model: {step.modelUsed || step.costDetail?.modelId || 'N/A'}
                            {step.costDetail && (
                            <> | {step.costDetail.inputTokens}↑ <span className="text-blue-600">{step.costDetail.cachedTokens ?? 0}📦 </span>{step.costDetail.outputTokens}↓</>
                            )}
                            {step.systemPromptHash && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPromptHash({ hash: step.systemPromptHash!, agentType: step.agentType });
                                }}
                                className="ml-2 p-0.5 hover:bg-gray-200 rounded transition-colors inline-flex items-center"
                                title={`View system prompt (${step.systemPromptHash.substring(0, 8)}...)`}
                              >
                                <FileText className="w-3.5 h-3.5 text-blue-600" />
                              </button>
                            )}
                            {step.temperature !== undefined && step.temperature !== null && (
                              <span className="ml-2 inline-flex items-center gap-0.5 text-orange-600" title="Temperature: Controls randomness in responses (lower = more deterministic)">
                                <Thermometer className="w-3 h-3" /> {step.temperature.toFixed(1)}
                              </span>
                            )}
                            {step.topP !== undefined && step.topP !== null && (
                              <span className="ml-2 inline-flex items-center gap-0.5 text-blue-600" title="Top P: Nucleus sampling threshold (lower = more focused)">
                                <Target className="w-3 h-3" /> {step.topP.toFixed(1)}
                              </span>
                            )}
                            {step.seed !== undefined && step.seed !== null && (
                              <span className="ml-2 inline-flex items-center gap-0.5 text-green-600" title="Seed: Random seed for reproducible outputs">
                                <Dices className="w-3 h-3" /> {step.seed}
                              </span>
                            )}
                            {step.frequencyPenalty !== undefined && step.frequencyPenalty !== null && (
                              <span className="ml-2 text-gray-600" title="Frequency Penalty: Reduces repetition of tokens (higher = less repetition)">
                                FP:{step.frequencyPenalty.toFixed(1)}
                              </span>
                            )}
                            {step.presencePenalty !== undefined && step.presencePenalty !== null && (
                              <span className="ml-2 text-gray-600" title="Presence Penalty: Encourages topic diversity (higher = more diverse)">
                                PP:{step.presencePenalty.toFixed(1)}
                              </span>
                            )}
                            {step.reasoningEffort && (
                              <span className="ml-2 inline-flex items-center gap-0.5 text-purple-600" title={`Reasoning Effort: Controls thinking depth (${step.reasoningEffort})`}>
                                <Brain className="w-3 h-3" /> {step.reasoningEffort.charAt(0).toUpperCase() + step.reasoningEffort.slice(1)}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      
                      {step.errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                          <strong>Error:</strong> {step.errorMessage}
                        </div>
                      )}

                      {isStepExpanded && (
                        <div className="mt-3 space-y-2">
                          {step.chatHistory && step.chatHistory.length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-gray-600 mb-1">💬 Chat History ({step.chatHistory.length} messages)</div>
                              <div className="space-y-1 max-h-64 overflow-y-auto bg-gray-50 p-2 rounded border border-gray-200">
                                {step.chatHistory.map((msg: { role: string; content: string }, idx: number) => (
                                  <div key={idx} className={`text-xs p-1.5 rounded ${msg.role === 'user' ? 'bg-blue-50 border-l-2 border-blue-400' : 'bg-green-50 border-l-2 border-green-400'}`}>
                                    <span className="font-semibold text-gray-700">{msg.role}:</span>{' '}
                                    <span className="text-gray-800 whitespace-pre-wrap break-words">{msg.content}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-xs font-medium text-gray-600">Input</div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWrap(stepKey, 'input');
                                }}
                                className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                                title={inputWrapped ? "Disable text wrap" : "Enable text wrap"}
                              >
                                {inputWrapped ? "↔️ Unwrap" : "⤵️ Wrap"}
                              </button>
                            </div>
                            <pre className={`text-xs bg-gray-50 p-2 rounded max-h-40 ${inputWrapped ? 'whitespace-pre-wrap break-words overflow-y-auto' : 'overflow-x-auto'}`}>
                              {(() => {
                                try {
                                  return step.inputJson ? JSON.stringify(JSON.parse(step.inputJson), null, 2) : '(empty)';
                                } catch {
                                  return step.inputJson || '(empty)';
                                }
                              })()}
                            </pre>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-xs font-medium text-gray-600">Output</div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWrap(stepKey, 'output');
                                }}
                                className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                                title={outputWrapped ? "Disable text wrap" : "Enable text wrap"}
                              >
                                {outputWrapped ? "↔️ Unwrap" : "⤵️ Wrap"}
                              </button>
                            </div>
                            <pre className={`text-xs bg-gray-50 p-2 rounded max-h-40 ${outputWrapped ? 'whitespace-pre-wrap break-words overflow-y-auto' : 'overflow-x-auto'}`}>
                              {(() => {
                                try {
                                  return step.outputJson ? JSON.stringify(JSON.parse(step.outputJson), null, 2) : '(empty)';
                                } catch {
                                  return step.outputJson || '(empty)';
                                }
                              })()}
                            </pre>
                          </div>
                          {step.errorMessage && (
                            <div>
                              <div className="text-xs font-medium text-red-600 mb-1">Error</div>
                              <pre className="text-xs bg-red-50 border border-red-200 p-2 rounded max-h-40 whitespace-pre-wrap break-words overflow-y-auto text-red-800">
                                {step.errorMessage}
                              </pre>
                            </div>
                          )}
                          {step.toolCalls && step.toolCalls.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-semibold text-purple-900 mb-1">🔧 Tool Calls ({step.toolCalls.length})</div>
                              <div className="space-y-2">
                                {step.toolCalls.map((tc: any, idx: number) => (
                                  <ToolCallCard key={idx} tc={tc} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );

                case 'searchContext':
                  const context = item.data;

                  return (
                    <div key={`searchContext-${index}`} className="bg-indigo-50 p-3 rounded border border-indigo-200">
                      <SearchParametersDisplay
                        vectorQueryText={context.vectorQueryText}
                        contraryQueryText={context.contraryQueryText}
                        maxResults={context.maxResults}
                        maxPassagesPerDocument={context.maxPassagesPerDocument}
                        filters={context.filters}
                        showTimestamp={true}
                        timestamp={context.timestamp}
                        attemptNumber={context.attemptNumber}
                        orchestratorDecision={executionFlow.orchestratorDecision?.decision}
                      />
                    </div>
                  );

                case 'embedding':
                  const newEmbeddingCost = item.data.embeddingTokensUsed > 0
                    ? (item.data.embeddingTokensUsed / 1_000_000) * 0.12
                    : 0;
                  const cachedEmbeddingCost = item.data.cachedEmbeddingTokens > 0
                    ? (item.data.cachedEmbeddingTokens / 1_000_000) * 0.12
                    : 0;
                  return (
                    <div key={`embedding-${index}`} className="bg-amber-50 p-3 rounded border border-amber-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm">🧮</span>
                          <span className="text-sm font-medium text-amber-900">Generating embeddings</span>
                          {item.data.vectorQueryCount > 0 && (
                            <span className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                              {item.data.vectorQueryCount} vector{item.data.vectorQueryCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {item.data.embeddingTokensUsed > 0 && (
                            <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                              New: {item.data.embeddingTokensUsed.toLocaleString()} tokens ({formatCost(newEmbeddingCost)})
                            </span>
                          )}
                          {item.data.cachedEmbeddingTokens > 0 && (
                            <span className="text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                              Cached: ~{item.data.cachedEmbeddingTokens.toLocaleString()} tokens ({formatCost(cachedEmbeddingCost)} saved)
                            </span>
                          )}
                          {item.data.attemptNumber > 1 && (
                            <span className="text-xs text-amber-600">
                              (attempt {item.data.attemptNumber})
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-amber-700">
                          {formatDuration(item.data.embeddingGenerationTimeMs)}
                        </span>
                      </div>
                    </div>
                  );

                case 'opinions':
                  // Use timing from the item itself (attached when created)
                  const timingData = item.timing;
                  const droppedOpinionCount = item.data.filter((o: any) => o.dropReason).length;
                  const droppedParagraphCount = item.data.reduce((acc: number, o: any) => 
                    acc + (o.paragraphs?.filter((p: any) => p.dropReason).length || 0), 0);
                  
                  return (
                    <div key={`opinions-${index}`} className="bg-purple-50 p-3 rounded border border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-purple-900">Searched Opinions</h4>
                          {(droppedOpinionCount > 0 || droppedParagraphCount > 0) && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium" title="Items excluded from AI processing due to bad OCR or reducer failure">
                              ⚠️ {droppedOpinionCount > 0 ? `${droppedOpinionCount} opinion${droppedOpinionCount !== 1 ? 's' : ''} dropped` : ''}
                              {droppedOpinionCount > 0 && droppedParagraphCount > 0 ? ', ' : ''}
                              {droppedParagraphCount > 0 ? `${droppedParagraphCount} paragraph${droppedParagraphCount !== 1 ? 's' : ''} dropped` : ''}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-purple-600">
                          <span>{item.data.length} result{item.data.length !== 1 ? 's' : ''}</span>
                          {timingData && (timingData.searchExecutionTimeMs !== undefined || timingData.parameterGenerationTimeMs !== undefined || timingData.embeddingGenerationTimeMs !== undefined) && (
                            <span className="font-medium">
                              ({formatDuration((timingData.parameterGenerationTimeMs || 0) + (timingData.embeddingGenerationTimeMs || 0) + (timingData.searchExecutionTimeMs || 0))})
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Timing Information from Search */}
                      {timingData && (timingData.searchExecutionTimeMs !== undefined || timingData.parameterGenerationTimeMs !== undefined || timingData.embeddingGenerationTimeMs !== undefined) && (
                        <div className="mb-2 p-2 bg-white rounded border border-purple-200">
                          <div className="text-xs font-semibold text-purple-900 mb-1">⏱️ Search Timing</div>
                          <div className="flex gap-4 text-xs">
                            {timingData.parameterGenerationTimeMs !== undefined && (
                              <div>
                                <span className="text-gray-600">Param Gen:</span>
                                <span className="ml-1 font-medium text-purple-700">{formatDuration(timingData.parameterGenerationTimeMs)}</span>
                              </div>
                            )}
                            {timingData.embeddingGenerationTimeMs !== undefined && (
                              <div>
                                <span className="text-gray-600">Embedding:</span>
                                <span className="ml-1 font-medium text-purple-700">{formatDuration(timingData.embeddingGenerationTimeMs)}</span>
                              </div>
                            )}
                            {timingData.searchExecutionTimeMs !== undefined && (
                              <div>
                                <span className="text-gray-600">Elasticsearch:</span>
                                <span className="ml-1 font-medium text-purple-700">{formatDuration(timingData.searchExecutionTimeMs)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <SearchedOpinionsList 
                        opinions={item.data}
                        searchExecutionTimeMs={timingData?.searchExecutionTimeMs}
                        parameterGenerationTimeMs={timingData?.parameterGenerationTimeMs}
                        embeddingGenerationTimeMs={timingData?.embeddingGenerationTimeMs}
                      />
                    </div>
                  );

                case 'response':
                  return (
                    <div key={`response-${index}`} className="bg-green-50 p-3 rounded border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-green-900">Final Response</h4>
                        <div className="text-xs text-green-600">
                          {formatTimestamp(item.timestamp.toISOString())}
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div><strong>Status:</strong> {item.data.status}</div>
                        {item.data.confidencePercentage && (
                          <div><strong>Confidence:</strong> {item.data.confidencePercentage}%</div>
                        )}
                        {item.data.sources && item.data.sources.length > 0 && (
                          <div><strong>Sources:</strong> {item.data.sources.length} found</div>
                        )}
                      </div>
                    </div>
                  );

                default:
                  return null;
              }
            })}
            
            {/* Cost Summary at the end */}
            {costSummary.totalCost > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">💰</span>
                  <h4 className="font-semibold text-green-900">Execution Cost Summary</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-600 mb-1 font-medium">Cost by Agent:</div>
                    <div className="space-y-1">
                      {Object.entries(costSummary.costByAgent).map(([agent, cost]) => (
                        <div key={agent} className="flex justify-between">
                          <span className="text-gray-700">{agent}:</span>
                          <span className="font-medium text-gray-900">{formatCost(cost)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between border-t pt-1 mt-1">
                        <span className="text-gray-700 font-semibold">Total:</span>
                        <span className="font-bold text-green-700">{formatCost(costSummary.totalCost)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 mb-1 font-medium">Total Tokens:</div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Input:</span>
                        <span className="font-medium text-gray-900">{costSummary.totalInputTokens.toLocaleString()}</span>
                      </div>
                      {costSummary.totalCachedTokens >= 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-600">↳ Cached:</span>
                          <span className="font-medium text-blue-600">{costSummary.totalCachedTokens.toLocaleString()} ({costSummary.totalInputTokens > 0 ? ((costSummary.totalCachedTokens / costSummary.totalInputTokens) * 100).toFixed(0) : 0}%)</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-700">Output:</span>
                        <span className="font-medium text-gray-900">{costSummary.totalOutputTokens.toLocaleString()}</span>
                      </div>
                      {costSummary.totalEmbeddingTokens > 0 && (
                        <div className="flex justify-between">
                          <span className="text-amber-700">🧮 Embedding (new):</span>
                          <span className="font-medium text-amber-700">{costSummary.totalEmbeddingTokens.toLocaleString()} ({formatCost(costSummary.totalEmbeddingCost)})</span>
                        </div>
                      )}
                      {costSummary.totalCachedEmbeddingTokens > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-600">📦 Embedding (cached):</span>
                          <span className="font-medium text-blue-600">~{costSummary.totalCachedEmbeddingTokens.toLocaleString()} ({formatCost(costSummary.totalCachedEmbeddingCost)} saved)</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-1">
                        <span className="text-gray-700 font-semibold">Total:</span>
                        <span className="font-bold text-gray-900">{(costSummary.totalInputTokens + costSummary.totalOutputTokens + costSummary.totalEmbeddingTokens).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Prompt Viewer Popup */}
      {selectedPromptHash && (
        <SystemPromptViewer
          hash={selectedPromptHash.hash}
          agentType={selectedPromptHash.agentType}
          onClose={() => setSelectedPromptHash(null)}
        />
      )}
    </div>
  );
};